import Vue from 'vue'
import Router from 'vue-router'
import Home from './views/Home.vue'
import i18n from './plugins/i18n'
import routes from './route_names'
import store from '@/store'
import AppRouter from './views/app/routes'
import SurveyRouter from './views/app/survey/routes'
import InsightsRouter from './views/app/insights/routes'
import AccountsRouter from './views/app/accounts/routes'
import ProofRouter from './views/app/proof/routes'

Vue.use(Router)

function authGuard(to, from, next) {
  if (to.meta.requiresAuth && !store.getters['user/isLoggedIn'])
    return store
      .dispatch('user/getDetails')
      .then(function(res) {
        next()
      })
      .catch(function() {
        if (to.meta.afterAuth) next(to.meta.afterAuth)
        else {
          next({ name: routes.LOGIN })
        }
      })
  next()
}

function defaultChildGuard(to, from, next) {
  if (to.meta.defaultChild) {
    if (to.matched.find(f => f.name === to.meta.defaultChild)) {
      // leave the route as is because child path is already there.
      return next()
    } else {
      return next({ name: to.meta.defaultChild, params: to.params })
    }
  } else {
    return next()
  }
}

function appRedirectGuard(to, from, next) {
  store
    .dispatch('user/getDetails')
    .then(function(res) {
      next({ name: routes.APP })
    })
    .catch(function() {
      next()
    })
}

const router = new Router({
  mode: 'history',
  base: '/',
  routes: [
    {
      path: '/',
      name: routes.HOME,
      component: Home,
      children: [
        {
          path: 'login',
          name: routes.LOGIN,
          beforeEnter: appRedirectGuard,
          component: () =>
            import(/* webpackChunkName: "auth" */ './views/auth/Login.vue'),
          meta: {
            layout: 'two_column/TwoColumnLayout'
          }
        },
        {
          path: 'signup',
          name: routes.SIGNUP,
          beforeEnter: appRedirectGuard,
          component: () =>
            import(/* webpackChunkName: "auth" */ './views/auth/Signup.vue'),
          meta: {
            layout: 'two_column/TwoColumnLayout'
          }
        },
        {
          path: 'forgot',
          name: routes.FORGOT_PASSWORD,
          component: () =>
            import(
              /* webpackChunkName: "auth" */ './views/auth/ForgotPassword.vue'
            ),
          meta: {
            layout: 'single_column/SingleColumnLayout'
          }
        },
        {
          path: 'user/verify',
          name: routes.VERIFY_USER,
          component: () =>
            import(/* webpackChunkName: "auth" */ './views/auth/Verifying.vue'),
          meta: {
            layout: 'single_column/SingleColumnLayout'
          }
        },
        {
          path: 'set-password',
          name: routes.FORGOT_PASSWORD_SET_PASSWORD,
          component: () =>
            import(
              /* webpackChunkName: "auth" */ './views/auth/SetPassword.vue'
            ),
          meta: {
            layout: 'single_column/SingleColumnLayout'
          }
        },
        {
          path: 'set-account-manager-password',
          name: routes.SET_ACCOUNT_MANAGER_PASSWORD,
          component: () =>
            import(
              /* webpackChunkName: "auth" */ './views/auth/SetAccountManagerPassword.vue'
            ),
          meta: {
            layout: 'single_column/SingleColumnLayout'
          }
        },

        {
          path: 'demo',
          name: routes.DEMO,
          component: () =>
            import(/* webpackChunkName: "auth" */ './views/Demo.vue'),
          meta: {
            layout: 'single_column/SingleColumnLayout'
          }
        },
        {
          path: 'app/wizard',
          name: routes.WIZARD,
          component: () =>
            import(
              /* webpackChunkName: "dashboard" */ './views/app/WizardView.vue'
            ),
          beforeEnter: function(to, from, next) {
            if (store.getters['user/currentCampaign']) {
              return next({
                name: routes.SURVEY_CUSTOMIZE,
                params: {
                  projectId: store.getters['user/currentCampaign'].project_id,
                  campaignId: store.getters['user/currentCampaign'].id
                }
              })
            }
            next()
          },
          defaultChild: SurveyRouter.name,
          meta: {
            layout: 'single_column/SingleColumnLayout',
            requiresAuth: true,
            requiredRole: 'ADMIN'
          }
        },
        {
          path: 'app',
          name: routes.APP,
          component: () =>
            import(
              /* webpackChunkName: "dashboard" */ './views/app/DashboardView.vue'
            ),
          beforeEnter: function(to, from, next) {
            if (to.name === routes.APP) {
              if (store.getters['user/currentCampaign'])
                return next({
                  name: routes.SURVEY_CUSTOMIZE,
                  params: {
                    projectId: store.getters['user/currentCampaign'].project_id,
                    campaignId: store.getters['user/currentCampaign'].id
                  }
                })
              else {
                return next({
                  name: routes.WIZARD
                })
              }
            }
            next()
          },
          defaultChild: SurveyRouter.name,
          meta: {
            layout: 'dashboard/DashboardLayout',
            requiresAuth: true,
            requiredRole: 'ADMIN'
          },
          children: [
            AppRouter,
            SurveyRouter,
            InsightsRouter,
            AccountsRouter,
            ProofRouter
          ]
        },
        {
          path: 'app/experience-map',
          name: routes.EXP_MAP,
          component: () =>
            import(
              /* webpackChunkName: "dashboard" */ './views/app/DashboardView.vue'
            ),
          meta: {
            layout: 'dashboard/DashboardLayout',
            requiresAuth: true,
            requiredRole: 'ADMIN'
          }
        },
        {
          path: 'help',
          name: routes.HELP,
          component: () =>
            import(
              /* webpackChunkName: "dashboard" */ './views/app/DashboardView.vue'
            ),
          meta: {
            layout: 'dashboard/DashboardLayout',
            requiresAuth: true,
            requiredRole: 'ADMIN'
          }
        },
        {
          path: 'oauth/authorize',
          name: routes.OAUTH_GRANT,
          component: () =>
            import(
              /* webpackChunkName: "auth" */ './views/auth/OauthGrant.vue'
            ),
          meta: {
            layout: 'single_column/SingleColumnLayout',
            requiresAuth: true,
            requiredRole: 'ADMIN',
            afterAuth: {
              name: routes.LOGIN,
              query: {
                next:
                  window.location.origin +
                  '/oauth/authorize' +
                  window.location.search
              }
            }
          }
        }
      ]
    }
    // {
    //     // not using it now
    //     path: '/:lang',
    //     name: 'lang',
    //     component: LangComponent,
    //     beforeEnter: function beforeEnter(to, from, next) {
    //         const lang = to.params.lang
    //         if (!['en', 'es'].includes(lang)) return next('en')
    //         if (i18n.locale === lang) return next()
    //         import(`@/locales/${lang}.json`).then((msgs) => {
    //             i18n.setLocaleMessage(lang, msgs)
    //             i18n.locale = lang
    //             return next()
    //         })
    //     },
    //
    //     children: []
    // },
  ]
})

router.beforeEach(authGuard)
router.beforeEach(defaultChildGuard)

export default router
